// src/components/BuildingViews/SawmillView.js
import React, { useState } from 'react';
import styles from './SawmillView.module.css';
import { ItemData } from '../../data/ItemData';
import { getItemImageSrc } from '../../utils/getItemImageSrc';

const SawmillView = ({ playerData, onPlayerAction, onGoBack }) => {
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Get current crafting status
  const activeCrafting = playerData?.buildings?.sawmill?.activeCrafting;

  const sawmillRecipes = [
    {
      id: 'refineWood',
      name: 'Cut Wood into Planks',
      description: 'Process raw wood into refined planks for construction',
      input: { 'wood': 1 },
      output: { 'plank': 1 },
      duration: 120, // 2 minutes
      icon: 'crafting/plank.png'
    }
  ];

  const canCraftRecipe = (recipe) => {
    for (const [itemKey, requiredAmount] of Object.entries(recipe.input)) {
      const available = playerData.inventory[itemKey] || 0;
      if (available < requiredAmount) {
        return false;
      }
    }
    return true;
  };

  const handleCraftClick = (recipe) => {
    if (!canCraftRecipe(recipe) || activeCrafting) {
      return;
    }

    setIsProcessing(true);
    setSelectedRecipe(recipe);
    
    // Call the action through the parent component
    onPlayerAction(recipe.id);
    
    // Reset processing state after a brief delay
    setTimeout(() => {
      setIsProcessing(false);
      setSelectedRecipe(null);
    }, 1000);
  };

  const handleStopCrafting = () => {
    onPlayerAction('stopCrafting', { buildingKey: 'sawmill' });
  };

  const handleToggleContinuous = () => {
    onPlayerAction('toggleContinuousCrafting', { buildingKey: 'sawmill' });
  };

  const buildingLevel = playerData.buildings?.sawmill?.upgradeLevel || 0;
  const efficiencyBonus = buildingLevel * 15; // 15% time reduction per level

  return (
    <div className={styles.sawmillContainer}>
      {onGoBack && (
        <button 
          className={styles.backButton}
          onClick={onGoBack}
          title="Back to Home"
        >
          ✕
        </button>
      )}
      <div className={styles.sawmillHeader}>
        <img src="/img/buildingItems/sawmill.png" alt="Sawmill" className={styles.sawmillIcon} />
        <div className={styles.sawmillInfo}>
          <h2>Sawmill Operations</h2>
          <p>Level {buildingLevel} {efficiencyBonus > 0 && `(-${efficiencyBonus}% time)`}</p>
          <p className={styles.description}>
            Precision wood cutting and lumber processing facility
          </p>
        </div>
      </div>

      {/* Active Crafting Progress */}
      {activeCrafting && (
        <div className={styles.craftingProgress}>
          <h3>🪵 Currently Processing</h3>
          <div className={styles.activeRecipe}>
            <div className={styles.recipeInfo}>
              <span className={styles.recipeName}>
                {activeCrafting.actionConfig.output ? 
                  Object.keys(activeCrafting.actionConfig.output).map(key => 
                    ItemData[key]?.name || key
                  ).join(', ') :
                  ItemData[activeCrafting.actionConfig.outputItemKey]?.name || activeCrafting.actionConfig.outputItemKey
                }
              </span>
              <div className={styles.progressBar}>
                <div 
                  className={styles.progressFill} 
                  style={{ width: `${(activeCrafting.progress || 0) * 100}%` }}
                ></div>
              </div>
              <span className={styles.progressText}>
                {Math.floor((activeCrafting.progress || 0) * 100)}% Complete
              </span>
            </div>
            <div className={styles.craftingControls}>
              <button 
                onClick={handleToggleContinuous}
                className={`${styles.controlButton} ${activeCrafting.continuousCrafting ? styles.active : ''}`}
              >
                {activeCrafting.continuousCrafting ? '🔄 Continuous ON' : '🔄 Continuous OFF'}
              </button>
              <button 
                onClick={handleStopCrafting}
                className={`${styles.controlButton} ${styles.stopButton}`}
              >
                ⏹️ Stop
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.recipeGrid}>
        {sawmillRecipes.map((recipe) => {
          const canCraft = canCraftRecipe(recipe);
          const isSelected = selectedRecipe?.id === recipe.id;
          
          return (
            <div 
              key={recipe.id} 
              className={`${styles.recipeCard} ${!canCraft ? styles.disabled : ''} ${isSelected ? styles.selected : ''}`}
            >
              <div className={styles.recipeHeader}>
                <img 
                  src={getItemImageSrc(recipe.icon)} 
                  alt={recipe.name} 
                  className={styles.recipeIcon} 
                />
                <div className={styles.recipeInfo}>
                  <h3>{recipe.name}</h3>
                  <p>{recipe.description}</p>
                </div>
              </div>

              <div className={styles.recipeDetails}>
                <div className={styles.requirements}>
                  <h4>Required:</h4>
                  <div className={styles.itemList}>
                    {Object.entries(recipe.input).map(([itemKey, amount]) => {
                      const item = ItemData[itemKey];
                      const available = playerData.inventory[itemKey] || 0;
                      const hasEnough = available >= amount;
                      
                      return (
                        <div key={itemKey} className={`${styles.itemRequirement} ${!hasEnough ? styles.insufficient : ''}`}>
                          <img 
                            src={getItemImageSrc(itemKey)} 
                            alt={item?.name || itemKey} 
                            className={styles.itemIcon} 
                          />
                          <span>{item?.name || itemKey}</span>
                          <span className={styles.itemAmount}>
                            {available}/{amount}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={styles.output}>
                  <h4>Produces:</h4>
                  <div className={styles.itemList}>
                    {Object.entries(recipe.output).map(([itemKey, amount]) => {
                      const item = ItemData[itemKey];
                      
                      return (
                        <div key={itemKey} className={styles.itemOutput}>
                          <img 
                            src={getItemImageSrc(itemKey)} 
                            alt={item?.name || itemKey} 
                            className={styles.itemIcon} 
                          />
                          <span>{item?.name || itemKey}</span>
                          <span className={styles.itemAmount}>x{amount}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={styles.recipeStats}>
                  <div className={styles.duration}>
                    <span>⏱️ {Math.floor(recipe.duration * (1 - efficiencyBonus / 100))}s</span>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => handleCraftClick(recipe)}
                disabled={!canCraft || isProcessing || activeCrafting}
                className={`${styles.craftButton} ${isProcessing && isSelected ? styles.processing : ''}`}
              >
                {activeCrafting ? 'Sawmill Busy' : (isProcessing && isSelected ? 'Processing...' : 'Start Cutting')}
              </button>
            </div>
          );
        })}
      </div>

      <div className={styles.sawmillTips}>
        <h4>💡 Sawmill Tips</h4>
        <ul>
          <li>Planks are essential for most construction projects</li>
          <li>Higher level sawmill processes wood faster</li>
          <li>Store extra planks for building upgrades</li>
        </ul>
      </div>
    </div>
  );
};

export default SawmillView; 